import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogActions,
  DialogContent,
  Button,
  makeStyles,
  tokens,
  Spinner,
  Table,
  TableHeader,
  TableRow,
  TableHeaderCell,
  TableBody,
  TableCell,
  Input,
  Badge,
  Text,
  Tooltip
} from '@fluentui/react-components';
import {
  ArrowClockwise24Regular,
  Save24Regular,
  Dismiss24Regular,
  Info16Regular
} from '@fluentui/react-icons';
import { configurationService } from '../../services/configurationService';
import type { QueueConfigurationDto } from '../../types';

const useStyles = makeStyles({
  dialogSurface: {
    maxWidth: '800px',
    width: '90vw',
  },
  tableContainer: {
    maxHeight: '400px',
    overflowY: 'auto',
  },
  inputCell: {
    display: 'flex',
    gap: '8px',
    alignItems: 'center',
  },
  smallInput: {
    width: '80px',
  },
  timeInput: {
    width: '100px',
  },
  defaultBadge: {
    marginLeft: '8px',
  },
  footer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: '16px',
    paddingTop: '16px',
    borderTop: `1px solid ${tokens.colorNeutralStroke1}`,
  },
  lastUpdated: {
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
  },
  infoIcon: {
    color: tokens.colorNeutralForeground3,
    cursor: 'help',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  description: {
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
    marginBottom: '16px',
  },
  errorText: {
    color: tokens.colorPaletteRedForeground1,
    fontSize: tokens.fontSizeBase200,
  },
  successText: {
    color: tokens.colorPaletteGreenForeground1,
    fontSize: tokens.fontSizeBase200,
  },
});

interface RetryConfigModalProps {
  open: boolean;
  onClose: () => void;
  onSaved?: () => void;
}

interface EditableConfig extends QueueConfigurationDto {
  delayHours: number;
  delayMinutes: number;
  isDirty: boolean;
}

export const RetryConfigModal: React.FC<RetryConfigModalProps> = ({
  open,
  onClose,
  onSaved
}) => {
  const styles = useStyles();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [configs, setConfigs] = useState<EditableConfig[]>([]);

  const parseDelay = (delay: string): { hours: number; minutes: number } => {
    const parts = delay.split(':');
    return {
      hours: parseInt(parts[0] || '0', 10),
      minutes: parseInt(parts[1] || '0', 10)
    };
  };

  const formatDelay = (hours: number, minutes: number): string => {
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:00`;
  };

  const loadConfigurations = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await configurationService.getAllConfigurations();

      const allConfigs: EditableConfig[] = [];

      // Adiciona config padrao primeiro
      if (response.default) {
        const parsed = parseDelay(response.default.retryDelay);
        allConfigs.push({
          ...response.default,
          delayHours: parsed.hours,
          delayMinutes: parsed.minutes,
          isDirty: false
        });
      }

      // Adiciona configs especificas
      for (const config of response.configurations) {
        const parsed = parseDelay(config.retryDelay);
        allConfigs.push({
          ...config,
          delayHours: parsed.hours,
          delayMinutes: parsed.minutes,
          isDirty: false
        });
      }

      if (allConfigs.length === 0) {
        setError('Nenhuma configuracao encontrada. A API pode estar inicializando as configuracoes padrao. Tente fechar e abrir o modal novamente.');
      }

      setConfigs(allConfigs);
    } catch (err: any) {
      const errorMsg = err?.response?.data || err?.message || 'Erro desconhecido';
      setError(`Erro ao carregar configuracoes: ${errorMsg}`);
      console.error('Error loading configurations:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      loadConfigurations();
    }
  }, [open]);

  const handleDelayHoursChange = (messageType: string, value: string) => {
    const hours = parseInt(value, 10) || 0;
    setConfigs(prev => prev.map(c =>
      c.messageType === messageType
        ? { ...c, delayHours: Math.min(Math.max(hours, 0), 23), isDirty: true }
        : c
    ));
    setSuccess(null);
  };

  const handleDelayMinutesChange = (messageType: string, value: string) => {
    const minutes = parseInt(value, 10) || 0;
    setConfigs(prev => prev.map(c =>
      c.messageType === messageType
        ? { ...c, delayMinutes: Math.min(Math.max(minutes, 0), 59), isDirty: true }
        : c
    ));
    setSuccess(null);
  };

  const handleCountLimitChange = (messageType: string, value: string) => {
    const countLimit = parseInt(value, 10) || 1;
    setConfigs(prev => prev.map(c =>
      c.messageType === messageType
        ? { ...c, retryCountLimit: Math.min(Math.max(countLimit, 1), 100), isDirty: true }
        : c
    ));
    setSuccess(null);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      const dirtyConfigs = configs.filter(c => c.isDirty);

      for (const config of dirtyConfigs) {
        await configurationService.updateRetryConfig({
          messageType: config.messageType,
          retryDelay: formatDelay(config.delayHours, config.delayMinutes),
          retryCountLimit: config.retryCountLimit
        });
      }

      setSuccess(`${dirtyConfigs.length} configuracao(oes) salva(s) com sucesso!`);
      setConfigs(prev => prev.map(c => ({ ...c, isDirty: false })));
      onSaved?.();
    } catch (err) {
      setError('Erro ao salvar configuracoes');
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const hasDirtyConfigs = configs.some(c => c.isDirty);

  const formatMessageType = (type: string) => {
    return type.replace(/([A-Z])/g, ' $1').trim();
  };

  return (
    <Dialog open={open} onOpenChange={(_, data) => !data.open && onClose()}>
      <DialogSurface className={styles.dialogSurface}>
        <DialogBody>
          <DialogTitle>
            <div className={styles.header}>
              <ArrowClockwise24Regular />
              Configuracao de Retry
            </div>
          </DialogTitle>

          <DialogContent>
            <Text className={styles.description}>
              Configure o intervalo entre tentativas e o numero maximo de retries para cada tipo de mensagem.
              Mensagens que excederem o limite serao enviadas para a DLQ.
            </Text>

            {loading ? (
              <Spinner label="Carregando configuracoes..." />
            ) : (
              <>
                <div className={styles.tableContainer}>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHeaderCell>Tipo de Mensagem</TableHeaderCell>
                        <TableHeaderCell>
                          <div className={styles.inputCell}>
                            Intervalo (HH:MM)
                            <Tooltip content="Tempo de espera entre cada tentativa de retry" relationship="label">
                              <Info16Regular className={styles.infoIcon} />
                            </Tooltip>
                          </div>
                        </TableHeaderCell>
                        <TableHeaderCell>
                          <div className={styles.inputCell}>
                            Max Tentativas
                            <Tooltip content="Numero maximo de tentativas antes de enviar para DLQ" relationship="label">
                              <Info16Regular className={styles.infoIcon} />
                            </Tooltip>
                          </div>
                        </TableHeaderCell>
                        <TableHeaderCell>Tempo Total</TableHeaderCell>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {configs.map((config) => {
                        const totalMinutes = (config.delayHours * 60 + config.delayMinutes) * config.retryCountLimit;
                        const totalHours = Math.floor(totalMinutes / 60);
                        const remainingMinutes = totalMinutes % 60;

                        return (
                          <TableRow key={config.messageType}>
                            <TableCell>
                              {formatMessageType(config.messageType)}
                              {config.isDefault && (
                                <Badge
                                  appearance="filled"
                                  color="brand"
                                  size="small"
                                  className={styles.defaultBadge}
                                >
                                  Padrao
                                </Badge>
                              )}
                              {config.isDirty && (
                                <Badge
                                  appearance="outline"
                                  color="warning"
                                  size="small"
                                  className={styles.defaultBadge}
                                >
                                  Modificado
                                </Badge>
                              )}
                            </TableCell>
                            <TableCell>
                              <div className={styles.inputCell}>
                                <Input
                                  type="number"
                                  min={0}
                                  max={23}
                                  value={config.delayHours.toString()}
                                  onChange={(_, data) => handleDelayHoursChange(config.messageType, data.value)}
                                  className={styles.smallInput}
                                />
                                <span>:</span>
                                <Input
                                  type="number"
                                  min={0}
                                  max={59}
                                  value={config.delayMinutes.toString()}
                                  onChange={(_, data) => handleDelayMinutesChange(config.messageType, data.value)}
                                  className={styles.smallInput}
                                />
                              </div>
                            </TableCell>
                            <TableCell>
                              <Input
                                type="number"
                                min={1}
                                max={100}
                                value={config.retryCountLimit.toString()}
                                onChange={(_, data) => handleCountLimitChange(config.messageType, data.value)}
                                className={styles.smallInput}
                              />
                            </TableCell>
                            <TableCell>
                              {totalHours > 0 ? `${totalHours}h ` : ''}{remainingMinutes}min
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>

                {error && <Text className={styles.errorText}>{error}</Text>}
                {success && <Text className={styles.successText}>{success}</Text>}
              </>
            )}
          </DialogContent>

          <DialogActions>
            <Button
              appearance="secondary"
              icon={<Dismiss24Regular />}
              onClick={onClose}
            >
              Fechar
            </Button>
            <Button
              appearance="primary"
              icon={saving ? <Spinner size="tiny" /> : <Save24Regular />}
              onClick={handleSave}
              disabled={loading || saving || !hasDirtyConfigs}
            >
              {saving ? 'Salvando...' : 'Salvar Alteracoes'}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};
